// ─────────────────────────────────────────────────────────────────────────────
// CommunityIntelligencePage, /community (Phase 1)
//
// The LWD Intelligence Community public landing page.
// Soho House × Bloomberg Terminal × Vogue Weddings: calm, curated, prestigious.
//
// Design language:
//   - Dark always (INK background). Never light mode.
//   - No social feed. No reaction counts. No like buttons.
//   - Editorial layout, typography-forward, sections breathe.
//   - GD (Gilda Display) for all headings, NU (Nunito) for all body copy.
//
// Static data is marked // TODO: replace with Supabase query
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import HomeNav from "../components/nav/HomeNav";
import { supabase } from "../lib/supabaseClient";
import CommunityFeed from "../components/community/CommunityFeed";

// ── Design tokens ─────────────────────────────────────────────────────────────
const GD   = "'Gilda Display', Georgia, serif";
const NU   = "'Nunito', sans-serif";

const GOLD        = "#d39a21";
const GOLD_DIM    = "rgba(211,154,33,0.12)";
const GOLD_BORDER = "rgba(211,154,33,0.22)";

// Theme-aware via CSS variables set on the page root (see ciVars + darkMode).
// The dark values are the fallbacks, so the page stays identical if a var is
// ever missing. Light values are supplied by ciVars(false) when lights are on.
const INK      = "var(--ci-ink, #0B0A08)";
const SURFACE  = "var(--ci-surface, #111009)";
const SURFACE2 = "var(--ci-surface2, #161410)";
const BORDER   = "var(--ci-border, rgba(255,255,255,0.06))";
const CREAM    = "var(--ci-text, #F2EFEA)";
const WARM     = "var(--ci-text2, #B8AFA4)";
const MUTED    = "var(--ci-muted, #7A7068)";
const FAINT    = "var(--ci-faint, #4A4440)";

// Palette values injected as CSS custom properties on the root element.
function ciVars(dark) {
  return dark
    ? {
        "--ci-ink": "#0B0A08", "--ci-surface": "#111009", "--ci-surface2": "#161410",
        "--ci-border": "rgba(255,255,255,0.06)", "--ci-border2": "rgba(255,255,255,0.1)", "--ci-text": "#F2EFEA",
        "--ci-text2": "#B8AFA4", "--ci-muted": "#7A7068", "--ci-faint": "#4A4440",
      }
    : {
        "--ci-ink": "#FAF8F4", "--ci-surface": "#FFFFFF", "--ci-surface2": "#F2EFEA",
        "--ci-border": "rgba(26,22,18,0.10)", "--ci-border2": "rgba(26,22,18,0.16)", "--ci-text": "#1A1612",
        "--ci-text2": "rgba(26,22,18,0.72)", "--ci-muted": "rgba(26,22,18,0.55)", "--ci-faint": "rgba(26,22,18,0.40)",
      };
}

// ── Static data ───────────────────────────────────────────────────────────────

// TODO: replace with Supabase query → community_circles
const CIRCLES = [
  { slug: "luxury-venues",           name: "Luxury Venues",          icon: "⬡", memberCount: 0, isInvitationOnly: false },
  { slug: "wedding-planners",        name: "Wedding Planners",        icon: "◎", memberCount: 0, isInvitationOnly: false },
  { slug: "editorial-photographers", name: "Editorial Photographers", icon: "◈", memberCount: 0, isInvitationOnly: true  },
  { slug: "destination-specialists", name: "Destination Specialists", icon: "◉", memberCount: 0, isInvitationOnly: false },
  { slug: "luxury-hotels",           name: "Luxury Hotels",           icon: "◇", memberCount: 0, isInvitationOnly: false },
  { slug: "italy-experts",           name: "Italy Experts",           icon: "◌", memberCount: 0, isInvitationOnly: true  },
];

// TODO: replace with Supabase query → community_discussions (is_featured=true, type='market_insight'|'destination_trend'|'venue_intelligence')
const BRIEFINGS = [
  {
    tag: "Market Report",
    date: "May 2026",
    title: "Italy Market Intelligence, Spring 2026",
    excerpt: "Enquiry volume up 18% YoY across Tuscany venues. Florence and Lake Como seeing strongest demand concentration.",
    readTime: "4 min",
  },
  {
    tag: "Trend Report",
    date: "April 2026",
    title: "The Rise of Micro-Luxury Weddings",
    excerpt: "Couples are increasingly prioritising intimacy over scale. Sub-50 guest weddings now represent 34% of enquiries on the platform.",
    readTime: "6 min",
  },
  {
    tag: "Venue Intelligence",
    date: "April 2026",
    title: "Q1 2026 Venue Conversion Report",
    excerpt: "Shortlist-to-enquiry rates improved 12% for venues with accommodation included in the listing hero.",
    readTime: "3 min",
  },
  {
    tag: "Destination Briefing",
    date: "March 2026",
    title: "Amalfi Coast: 2026 Season Outlook",
    excerpt: "Peak season bookings for September–October now running 6 weeks ahead of 2025 pace. Early outreach strategy essential.",
    readTime: "5 min",
  },
];

// TODO: replace with Supabase query → community_discussions (ORDER BY reply_count DESC, moderation_status='approved')
const TRENDING_DISCUSSIONS = [
  {
    type: "market_insight",
    circle: "Italy Experts",
    title: "Outdoor ceremony demand, what are you seeing in 2026?",
    author: "Sofia Marchetti",
    role: "Venue Director",
    replies: 14,
    time: "2 hours ago",
  },
  {
    type: "destination_trend",
    circle: "Destination Specialists",
    title: "Lake Como vs Tuscany: client preference shift",
    author: "James Hartley",
    role: "Wedding Planner",
    replies: 9,
    time: "Yesterday",
  },
  {
    type: "venue_intelligence",
    circle: "Luxury Venues",
    title: "Accommodation inclusion: impact on conversion",
    author: "Elena Ferrara",
    role: "Estate Director",
    replies: 21,
    time: "2 days ago",
  },
  {
    type: "operational",
    circle: "Wedding Planners",
    title: "Managing client expectations on venue exclusivity",
    author: "Amélie Dubois",
    role: "Senior Planner",
    replies: 7,
    time: "3 days ago",
  },
];

// TODO: replace with Supabase query → community_members (tier IN ('excellence','senior_member'), ORDER BY status_score DESC, LIMIT 6)
const FEATURED_MEMBERS = [
  { name: "Sofia Marchetti",  role: "Venue Director",    company: "Villa Corsini d'Ormeto", region: "Tuscany",           tier: "excellence",    initials: "SM" },
  { name: "James Hartley",    role: "Wedding Planner",   company: "Hartley & Co.",          region: "UK / Italy",         tier: "senior_member", initials: "JH" },
  { name: "Elena Ferrara",    role: "Estate Director",   company: "Palazzo Borghese",       region: "Rome",               tier: "member",        initials: "EF" },
  { name: "Amélie Dubois",    role: "Senior Planner",    company: "Dubois Événements",      region: "Paris / Côte d'Azur", tier: "senior_member", initials: "AD" },
  { name: "Marco Ricci",      role: "Photographer",      company: "Ricci Photography",      region: "Italy",              tier: "member",        initials: "MR" },
  { name: "Isabella Grant",   role: "Floral Director",   company: "Grant & Bloom",          region: "London / Tuscany",   tier: "excellence",    initials: "IG" },
];

// TODO: replace with Supabase query → community_roundtables (upcoming, ORDER BY date ASC)
const ROUNDTABLES = [
  {
    date: "May 22",
    time: "14:00 CET",
    title: "Italy 2026 Season Preview",
    description: "An analysis of enquiry patterns and venue availability for Q3–Q4 2026.",
    host: "LWD Intelligence Team",
    access: "Senior Members +",
    seats: "12 seats remaining",
  },
  {
    date: "June 5",
    time: "15:00 CET",
    title: "Pricing Strategies for Micro-Luxury Weddings",
    description: "How leading planners are structuring fees for sub-50-guest events.",
    host: "James Hartley",
    access: "All Members",
    seats: "24 seats remaining",
  },
  {
    date: "June 19",
    time: "11:00 CET",
    title: "Photography Direction for Film-Inspired Weddings",
    description: "Documentary approach, client briefing, and creative alignment with venues.",
    host: "Marco Ricci",
    access: "Editorial Photographers Circle",
    seats: "8 seats remaining",
  },
];

// TODO: replace with Supabase query → community_discussions (is_featured=true, LIMIT 3)
const FEATURED_CONVERSATIONS = [
  {
    type: "venue_intelligence",
    circle: "Luxury Venues",
    title: "Accommodation inclusion: impact on conversion",
    excerpt: "After reviewing 6 months of shortlist data, venues that lead with accommodation availability are seeing 12% higher enquiry conversion. The hypothesis is that couples are less willing to coordinate external accommodation for intimate guest lists. Seeking input from directors who've adjusted how they present this.",
    author: "Elena Ferrara",
    role: "Estate Director",
    replies: 21,
  },
  {
    type: "market_insight",
    circle: "Italy Experts",
    title: "Outdoor ceremony demand, what are you seeing in 2026?",
    excerpt: "We've seen a strong shift back toward outdoor ceremonies this season, clients want full immersion in the landscape, not just a backdrop. That said, contingency planning conversations are happening earlier than ever. Would appreciate a data read from colleagues who tracked weather-related plan changes last autumn.",
    author: "Sofia Marchetti",
    role: "Venue Director",
    replies: 14,
  },
  {
    type: "destination_trend",
    circle: "Destination Specialists",
    title: "Lake Como vs Tuscany: client preference shift",
    excerpt: "Northern Italian enquiries are up 22% this year, and we're noticing a meaningful shift away from Tuscany for certain client profiles, particularly those seeking more exclusive access and privacy. Tuscany still dominates on volume, but Como is taking a disproportionate share of high-spend, low-guest-count weddings.",
    author: "James Hartley",
    role: "Wedding Planner",
    replies: 9,
  },
];

// ── Helper functions ──────────────────────────────────────────────────────────

const DISCUSSION_TYPE_COLORS = {
  market_insight:       "#4ade80",
  venue_intelligence:   GOLD,
  destination_trend:    "#60a5fa",
  operational:          "#a78bfa",
  vendor_recommendation:"#fb7185",
  pricing:              "#fbbf24",
  conversion:           "#34d399",
  discussion:           MUTED,
};

function discussionTypeLabel(type) {
  const labels = {
    market_insight:       "Market Insight",
    venue_intelligence:   "Venue Intelligence",
    destination_trend:    "Destination Trend",
    operational:          "Operational",
    vendor_recommendation:"Vendor Rec",
    pricing:              "Pricing",
    conversion:           "Conversion",
    discussion:           "Discussion",
  };
  return labels[type] || type;
}

function tierLabel(tier) {
  const labels = { excellence: "Excellence", senior_member: "Senior", member: "Member", pending: "Pending", invited: "Invited" };
  return labels[tier] || tier;
}

function tierColor(tier) {
  if (tier === "excellence")    return GOLD;
  if (tier === "senior_member") return "#60a5fa";
  return MUTED;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function BriefingCard({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: SURFACE,
        border: `1px solid ${hovered ? GOLD_BORDER : BORDER}`,
        borderRadius: 8,
        padding: "24px 26px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        cursor: "pointer",
        transition: "border-color 0.18s ease, background 0.18s ease",
        background: hovered ? SURFACE2 : SURFACE,
      }}
    >
      {/* Header row: tag + date */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{
          fontFamily: NU,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: GOLD,
          background: GOLD_DIM,
          border: `1px solid ${GOLD_BORDER}`,
          borderRadius: 3,
          padding: "3px 8px",
        }}>
          {item.tag}
        </span>
        <span style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>{item.date}</span>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: GD,
        fontSize: 17,
        fontWeight: 400,
        color: CREAM,
        margin: 0,
        lineHeight: 1.4,
      }}>
        {item.title}
      </h3>

      {/* Excerpt */}
      <p style={{
        fontFamily: NU,
        fontSize: 13,
        color: WARM,
        margin: 0,
        lineHeight: 1.65,
        flexGrow: 1,
      }}>
        {item.excerpt}
      </p>

      {/* Footer: read time */}
      <div style={{ fontFamily: NU, fontSize: 10, color: FAINT, letterSpacing: "0.1em" }}>
        {item.readTime} read
      </div>
    </div>
  );
}

function CircleTile({ circle }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `1px solid ${hovered ? GOLD_BORDER : BORDER}`,
        borderRadius: 8,
        padding: "20px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        transition: "border-color 0.18s ease, background 0.18s ease",
        background: hovered ? SURFACE2 : "transparent",
        position: "relative",
      }}
    >
      {/* Invitation-only lock indicator */}
      {circle.isInvitationOnly && (
        <div style={{
          position: "absolute",
          top: 8,
          right: 10,
          fontFamily: NU,
          fontSize: 8,
          color: MUTED,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
        }}>
          ⊘
        </div>
      )}

      {/* Icon */}
      <span style={{ fontSize: 28, color: GOLD, lineHeight: 1 }}>{circle.icon}</span>

      {/* Name */}
      <span style={{
        fontFamily: GD,
        fontSize: 13,
        fontWeight: 400,
        color: CREAM,
        textAlign: "center",
        lineHeight: 1.3,
      }}>
        {circle.name}
      </span>

      {/* Member count */}
      {circle.memberCount > 0 && (
        <span style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>
          {circle.memberCount} members
        </span>
      )}
    </div>
  );
}

function DiscussionRow({ item, isLast }) {
  const typeColor = DISCUSSION_TYPE_COLORS[item.type] || MUTED;
  return (
    <div style={{
      padding: "16px 0",
      borderBottom: isLast ? "none" : `1px solid ${BORDER}`,
    }}>
      {/* Top: type tag + circle */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{
          fontFamily: NU,
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: typeColor,
          border: `1px solid ${typeColor}33`,
          borderRadius: 2,
          padding: "2px 6px",
        }}>
          {discussionTypeLabel(item.type)}
        </span>
        <span style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>{item.circle}</span>
      </div>

      {/* Title */}
      <div style={{
        fontFamily: NU,
        fontSize: 13,
        fontWeight: 600,
        color: CREAM,
        lineHeight: 1.4,
        marginBottom: 6,
        cursor: "pointer",
      }}>
        {item.title}
      </div>

      {/* Meta: author + replies + time */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontFamily: NU, fontSize: 11, color: MUTED }}>
          {item.author}
        </span>
        <span style={{ color: FAINT, fontSize: 10 }}>·</span>
        <span style={{ fontFamily: NU, fontSize: 11, color: MUTED, fontStyle: "italic" }}>
          {item.role}
        </span>
        <span style={{ color: FAINT, fontSize: 10 }}>·</span>
        <span style={{ fontFamily: NU, fontSize: 10, color: FAINT }}>
          {item.replies} replies
        </span>
        <span style={{ color: FAINT, fontSize: 10 }}>·</span>
        <span style={{ fontFamily: NU, fontSize: 10, color: FAINT }}>{item.time}</span>
      </div>
    </div>
  );
}

function MemberRow({ member, isLast }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 0",
      borderBottom: isLast ? "none" : `1px solid ${BORDER}`,
    }}>
      {/* Avatar */}
      <div style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: SURFACE2,
        border: `1px solid ${GOLD_BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: GD,
        fontSize: 12,
        color: GOLD,
        flexShrink: 0,
        letterSpacing: "0.05em",
      }}>
        {member.initials}
      </div>

      {/* Info */}
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: NU,
          fontSize: 12,
          fontWeight: 600,
          color: CREAM,
          marginBottom: 2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {member.name}
        </div>
        <div style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>
          {member.role} · {member.region}
        </div>
      </div>

      {/* Tier badge */}
      <span style={{
        fontFamily: NU,
        fontSize: 8,
        fontWeight: 700,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: tierColor(member.tier),
        border: `1px solid ${tierColor(member.tier)}44`,
        borderRadius: 2,
        padding: "2px 6px",
        flexShrink: 0,
      }}>
        {tierLabel(member.tier)}
      </span>
    </div>
  );
}

function RoundtableRow({ item, isLast }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 24,
      padding: "22px 0",
      borderBottom: isLast ? "none" : `1px solid ${BORDER}`,
    }}>
      {/* Date block */}
      <div style={{
        flexShrink: 0,
        width: 56,
        textAlign: "center",
      }}>
        <div style={{
          fontFamily: GD,
          fontSize: 18,
          fontWeight: 400,
          color: GOLD,
          lineHeight: 1.1,
        }}>
          {item.date.split(" ")[0]}
        </div>
        <div style={{
          fontFamily: NU,
          fontSize: 9,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: MUTED,
          marginTop: 2,
        }}>
          {item.date.split(" ")[1]}
        </div>
        <div style={{
          fontFamily: NU,
          fontSize: 10,
          color: FAINT,
          marginTop: 4,
        }}>
          {item.time}
        </div>
      </div>

      {/* Title + description */}
      <div style={{ flexGrow: 1 }}>
        <div style={{
          fontFamily: GD,
          fontSize: 15,
          fontWeight: 400,
          color: CREAM,
          marginBottom: 5,
          lineHeight: 1.3,
        }}>
          {item.title}
        </div>
        <div style={{
          fontFamily: NU,
          fontSize: 12,
          color: WARM,
          lineHeight: 1.6,
        }}>
          {item.description}
        </div>
      </div>

      {/* Host + access + seats */}
      <div style={{
        flexShrink: 0,
        textAlign: "right",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 160,
      }}>
        <div style={{ fontFamily: NU, fontSize: 11, color: WARM }}>{item.host}</div>
        <div style={{
          fontFamily: NU,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: GOLD,
        }}>
          {item.access}
        </div>
        <div style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>{item.seats}</div>
      </div>
    </div>
  );
}

function FeaturedConversationCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const typeColor = DISCUSSION_TYPE_COLORS[item.type] || MUTED;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? SURFACE2 : SURFACE,
        border: `1px solid ${hovered ? GOLD_BORDER : BORDER}`,
        borderRadius: 8,
        padding: "28px 30px",
        cursor: "pointer",
        transition: "border-color 0.18s ease, background 0.18s ease",
      }}
    >
      {/* Tag + circle */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{
          fontFamily: NU,
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: typeColor,
          border: `1px solid ${typeColor}33`,
          borderRadius: 2,
          padding: "2px 7px",
        }}>
          {discussionTypeLabel(item.type)}
        </span>
        <span style={{ fontFamily: NU, fontSize: 10, color: MUTED }}>{item.circle}</span>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: GD,
        fontSize: 19,
        fontWeight: 400,
        color: CREAM,
        margin: "0 0 12px",
        lineHeight: 1.35,
      }}>
        {item.title}
      </h3>

      {/* Excerpt */}
      <p style={{
        fontFamily: NU,
        fontSize: 13,
        color: WARM,
        margin: "0 0 16px",
        lineHeight: 1.7,
      }}>
        {item.excerpt}
      </p>

      {/* Author */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: NU, fontSize: 11, color: MUTED }}>{item.author}</span>
        <span style={{ color: FAINT }}>·</span>
        <span style={{ fontFamily: NU, fontSize: 11, color: MUTED, fontStyle: "italic" }}>{item.role}</span>
        <span style={{ color: FAINT }}>·</span>
        <span style={{ fontFamily: NU, fontSize: 10, color: FAINT }}>{item.replies} replies</span>
      </div>
    </div>
  );
}

// ── Section header component ──────────────────────────────────────────────────
function SectionHeader({ title, action, actionLabel }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginBottom: 24,
    }}>
      <h2 style={{
        fontFamily: GD,
        fontSize: 22,
        fontWeight: 400,
        color: CREAM,
        margin: 0,
      }}>
        {title}
      </h2>
      {action && (
        <button
          onClick={action}
          style={{
            fontFamily: NU,
            fontSize: 11,
            color: GOLD,
            background: "none",
            border: "none",
            cursor: "pointer",
            letterSpacing: "0.05em",
            padding: 0,
            opacity: 0.8,
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = "1"}
          onMouseLeave={e => e.currentTarget.style.opacity = "0.8"}
        >
          {actionLabel || "View all →"}
        </button>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function CommunityIntelligencePage({ onNavigate }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // ── Live data from Supabase ─────────────────────────────────────────────────
  const [darkMode,        setDarkMode]        = useState(true); // dark by default; lights toggle flips it
  const [liveCircles,     setLiveCircles]     = useState(null); // null = loading
  const [liveMembers,     setLiveMembers]     = useState(null);
  const [liveDiscussions, setLiveDiscussions] = useState(null);
  const [memberCount,     setMemberCount]     = useState(null);

  useEffect(() => {
    if (!supabase) return;
    // Circles with live member counts
    supabase.from("community_circles")
      .select("id,name,slug,icon,member_count,is_invitation_only")
      .order("member_count", { ascending: false })
      .then(({ data }) => { if (data) setLiveCircles(data); });

    // Recent verified members (public)
    supabase.from("community_members")
      .select("id,display_name,role,company,region,tier,avatar_url")
      .eq("is_public", true)
      .not("verified_at", "is", null)
      .order("joined_at", { ascending: false })
      .limit(6)
      .then(({ data }) => { if (data) setLiveMembers(data); });

    // Featured/pinned approved discussions
    supabase.from("community_discussions")
      .select("id,title,type,reply_count,created_at,author_id,circle_id,community_members(display_name,role),community_circles(name)")
      .eq("moderation_status", "approved")
      .order("created_at", { ascending: false })
      .limit(4)
      .then(({ data }) => { if (data) setLiveDiscussions(data); });

    // Total member count for hero stats
    supabase.from("community_members")
      .select("*", { count: "exact", head: true })
      .not("verified_at", "is", null)
      .then(({ count }) => { if (count != null) setMemberCount(count); });
  }, []);

  // Use live data when available, fall back to static placeholders
  const displayCircles     = liveCircles     ?? CIRCLES;
  const displayMembers     = liveMembers     ?? FEATURED_MEMBERS;
  const displayDiscussions = liveDiscussions ?? TRENDING_DISCUSSIONS.map(d => ({
    id: d.id, title: d.title, type: d.type, reply_count: d.replies,
    community_circles: { name: d.circle },
    community_members: { display_name: d.author, role: d.role },
    created_at: d.time,
  }));
  const displayMemberCount = memberCount ?? 312;

  return (
    <div style={{ ...ciVars(darkMode), background: INK, minHeight: "100vh", color: CREAM, transition: "background 0.25s ease, color 0.25s ease" }}>
      {/* Navigation, lights on/off toggle drives the whole page theme */}
      <HomeNav
        darkMode={darkMode}
        onToggleDark={() => setDarkMode((d) => !d)}
        lightScheme={!darkMode}
        hasHero={false}
      />

      {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
      <section style={{
        // Extra top padding so the eyebrow clears the fixed masthead + menu
        paddingTop: isMobile ? 132 : 210,
        paddingBottom: isMobile ? 64 : 96,
        paddingLeft: isMobile ? 24 : 64,
        paddingRight: isMobile ? 24 : 64,
        maxWidth: 900,
        margin: "0 auto",
      }}>
        {/* Eyebrow */}
        <div style={{
          fontFamily: NU,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: GOLD,
          marginBottom: 24,
        }}>
          ◈ LWD Intelligence Community
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: GD,
          fontSize: isMobile ? 36 : 52,
          fontWeight: 300,
          color: CREAM,
          margin: "0 0 20px",
          lineHeight: 1.2,
          maxWidth: 680,
        }}>
          A private network for<br />the luxury wedding industry.
        </h1>

        {/* Subtext */}
        <p style={{
          fontFamily: NU,
          fontSize: 15,
          color: WARM,
          margin: "0 0 32px",
          lineHeight: 1.65,
          maxWidth: 520,
        }}>
          Curated. Invitation-only. Built for professionals who shape the industry.
        </p>

        {/* Stats row */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: isMobile ? 16 : 28,
          alignItems: "center",
          marginBottom: 36,
        }}>
          {[
            `${displayMemberCount} Verified Member${displayMemberCount === 1 ? "" : "s"}`,
            "Invitation Only",
            "6 Professional Circles",
          ].map((stat, i) => (
            <span key={i} style={{ display: "flex", alignItems: "center", gap: i > 0 ? 28 : 0 }}>
              {i > 0 && <span style={{ color: FAINT, marginRight: 0 }}>·</span>}
              <span style={{
                fontFamily: NU,
                fontSize: 12,
                color: MUTED,
                letterSpacing: "0.05em",
              }}>
                {stat}
              </span>
            </span>
          ))}
        </div>

        {/* CTA */}
        <button style={{
          fontFamily: NU,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: GOLD,
          background: "transparent",
          border: `1px solid ${GOLD_BORDER}`,
          borderRadius: 3,
          padding: "14px 28px",
          cursor: "pointer",
          transition: "background 0.18s ease, border-color 0.18s ease",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = GOLD_DIM; e.currentTarget.style.borderColor = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = GOLD_BORDER; }}
          onClick={() => { window.location.href = "/apply-for-inclusion"; }}
        >
          Apply for Membership
        </button>
      </section>

      {/* Thin gold divider */}
      <div style={{ maxWidth: 900, margin: "0 auto 0", padding: "0 64px" }}>
        <div style={{ height: 1, background: GOLD_BORDER }} />
      </div>

      {/* ── 2. INTELLIGENCE BRIEFINGS ───────────────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "56px 24px" : "72px 64px",
      }}>
        <SectionHeader title="Intelligence Briefings" actionLabel="View all →" />

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 16,
        }}>
          {BRIEFINGS.map((item, i) => (
            <BriefingCard key={i} item={item} />
          ))}
        </div>
      </section>

      {/* ── 3. PROFESSIONAL CIRCLES ─────────────────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "40px 24px 56px" : "56px 64px 72px",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <SectionHeader title="Professional Circles" />

        <p style={{
          fontFamily: NU,
          fontSize: 13,
          color: MUTED,
          margin: "0 0 28px",
          lineHeight: 1.6,
          maxWidth: 560,
        }}>
          Invitation-based groups for verified professionals. Join the circle that matches your expertise.
        </p>

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(6, 1fr)",
          gap: 10,
        }}>
          {CIRCLES.map((circle) => (
            <CircleTile key={circle.slug} circle={circle} />
          ))}
        </div>
      </section>

      {/* ── 4. TWO-COLUMN: TRENDING + MEMBERS ──────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "40px 24px 56px" : "56px 64px 72px",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "3fr 2fr",
          gap: isMobile ? 48 : 48,
        }}>
          {/* Left: Trending Discussions */}
          <div>
            <SectionHeader title="Trending Discussions" />
            <div>
              {TRENDING_DISCUSSIONS.map((item, i) => (
                <DiscussionRow
                  key={i}
                  item={item}
                  isLast={i === TRENDING_DISCUSSIONS.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Right: Verified Members */}
          <div>
            <SectionHeader title="Verified Members" />
            <div>
              {FEATURED_MEMBERS.map((member, i) => (
                <MemberRow
                  key={i}
                  member={member}
                  isLast={i === FEATURED_MEMBERS.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4b. COMMUNITY FEED ──────────────────────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "40px 24px 56px" : "56px 64px 72px",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <CommunityFeed isMobile={isMobile} />
      </section>

      {/* ── 5. UPCOMING ROUNDTABLES ─────────────────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "40px 24px 56px" : "56px 64px 72px",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <SectionHeader title="Upcoming Roundtables" />

        <div>
          {ROUNDTABLES.map((item, i) => (
            <RoundtableRow
              key={i}
              item={item}
              isLast={i === ROUNDTABLES.length - 1}
            />
          ))}
        </div>
      </section>

      {/* ── 6. FEATURED CONVERSATIONS ───────────────────────────────────────── */}
      <section style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: isMobile ? "40px 24px 56px" : "56px 64px 72px",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <SectionHeader title="Featured Conversations" />

        <p style={{
          fontFamily: NU,
          fontSize: 13,
          color: MUTED,
          margin: "0 0 28px",
          lineHeight: 1.6,
          maxWidth: 520,
        }}>
          Selected by the editorial team for their intelligence value.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {FEATURED_CONVERSATIONS.map((item, i) => (
            <FeaturedConversationCard key={i} item={item} />
          ))}
        </div>
      </section>

      {/* ── 7. BOTTOM CTA ───────────────────────────────────────────────────── */}
      <section style={{
        background: SURFACE,
        borderTop: `1px solid ${BORDER}`,
        padding: isMobile ? "56px 24px" : "80px 64px",
        textAlign: "center",
      }}>
        <h2 style={{
          fontFamily: GD,
          fontSize: isMobile ? 26 : 34,
          fontWeight: 300,
          color: CREAM,
          margin: "0 0 24px",
          lineHeight: 1.25,
        }}>
          Built for the professionals who define the industry.
        </h2>

        <button style={{
          fontFamily: NU,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: GOLD,
          background: "transparent",
          border: `1px solid ${GOLD_BORDER}`,
          borderRadius: 3,
          padding: "14px 28px",
          cursor: "pointer",
          transition: "background 0.18s ease, border-color 0.18s ease",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = GOLD_DIM; e.currentTarget.style.borderColor = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = GOLD_BORDER; }}
          onClick={() => { window.location.href = "/apply-for-inclusion"; }}
        >
          Apply for Membership →
        </button>
      </section>

      {/* Page bottom padding */}
      <div style={{ height: 1, background: BORDER }} />
    </div>
  );
}
